/**
 * CorporateStructure — manages the organisation's top-level department list.
 *
 * Provides a TanStack Table view of departments with inline Create, Edit,
 * and Delete actions (via modal portals). All mutations hit the `department`
 * table in Supabase and re-fetch on success.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useReactTable, getCoreRowModel, flexRender } from '@tanstack/react-table';
import { supabase } from '../../config/supabase';
import { useAuth } from '../../context/AuthContext';
import './OrgStructure.css';

const OrganizationObjective = () => {
  const { organizationId } = useAuth();
  const [data, setData] = useState([]);
  const [newDepartment, setNewDepartment] = useState({ depart_name: '' });
  const [isCreateFunctionOpen, setIsCreateFunctionOpen] = useState(false);
  const [editDepartment, setEditDepartment] = useState({ department_id: null, depart_name: '' });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, department: null });

  const fetchDepartments = useCallback(async () => {
    if (!organizationId) return;
    try {
      const { data, error } = await supabase
        .from('department')
        .select('department_id, depart_name, roles(role_id)')
        .eq('organization_id', organizationId)
        .order('department_id', { ascending: true });

      if (error) throw error;

      const formattedData = (data || []).map((department) => ({
        ...department,
        role_count: department.roles?.length || 0,
      }));

      setData(formattedData);
    } catch (error) {
      console.error('Error fetching data:', error.message);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  const handleEdit = useCallback((department) => {
    setEditDepartment({
      department_id: department.department_id,
      depart_name: department.depart_name || '',
    });
    setIsModalOpen(true);
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (!editDepartment.department_id) {
      alert('No function selected for editing.');
      return;
    }

    if (!editDepartment.depart_name.trim()) {
      alert('Please enter a function name');
      return;
    }

    try {
      const { error } = await supabase
        .from('department')
        .update({ depart_name: editDepartment.depart_name.trim() })
        .eq('department_id', editDepartment.department_id);

      if (error) throw error;

      await fetchDepartments();
      setIsModalOpen(false);
      setEditDepartment({ department_id: null, depart_name: '' });
    } catch (error) {
      console.error('Error updating department:', error.message);
      alert('Failed to update function: ' + error.message);
    }
  }, [editDepartment, fetchDepartments]);

  const handleDeleteClick = useCallback((department) => {
    setDeleteConfirm({ isOpen: true, department });
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteConfirm.department) return;

    try {
      const deptId = deleteConfirm.department.department_id;

      // Delete competency_descriptions referencing this department (FK constraint)
      await supabase.from('competency_descriptions').delete().eq('department_id', deptId);

      // Delete func_competence_items for groups in this department, then the groups themselves
      const { data: funcGroups } = await supabase
        .from('func_competence_groups')
        .select('group_id')
        .eq('department_id', deptId);
      if (funcGroups && funcGroups.length > 0) {
        const groupIds = funcGroups.map(g => g.group_id);
        await supabase.from('func_competence_items').delete().in('group_id', groupIds);
      }
      await supabase.from('func_competence_groups').delete().eq('department_id', deptId);

      const { error } = await supabase
        .from('department')
        .delete()
        .eq('department_id', deptId);

      if (error) throw error;

      await fetchDepartments();
      setDeleteConfirm({ isOpen: false, department: null });
    } catch (error) {
      console.error('Error deleting department:', error.message);
      alert('Failed to delete function: ' + error.message);
    }
  }, [deleteConfirm, fetchDepartments]);

  const handleAddDepartment = async (e) => {
    e.preventDefault();

    if (!newDepartment.depart_name.trim()) {
      alert('Please enter a department name');
      return;
    }

    try {
      const { error } = await supabase
        .from('department')
        .insert([{ depart_name: newDepartment.depart_name.trim(), organization_id: organizationId }]);

      if (error) throw error;

      await fetchDepartments();
      setNewDepartment({ depart_name: '' });
      setIsCreateFunctionOpen(false);
    } catch (error) {
      console.error('Error adding department:', error.message);
      alert('Failed to add department: ' + error.message);
    }
  };

  const columns = useMemo(
    () => [
      { accessorKey: 'depart_name', header: 'Functions' },
      {
        accessorKey: 'role_count',
        header: 'Roles',
        cell: ({ row }) => (
          <div className="department-stats">
            <div className="stat-item">
              <span className="stat-number">{row.original.role_count || 0}</span>
            </div>
          </div>
        ),
      },
      {
        id: 'action',
        header: 'Actions',
        cell: ({ row }) => (
          <div className="action-buttons-org">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(row.original);
              }}
            >
              Edit
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(row.original);
              }}
            >
              Delete
            </button>
          </div>
        ),
      },
    ],
    [handleEdit, handleDeleteClick]
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="organization-objective">
      <h1><i className="bi bi-grid-3x3-gap" />Organization Structure</h1>
      <p>
        <strong>How is your organisation structured ? </strong>
        Set up the key functions that make up your organisation here. (For example: Finance, HR,
        Legal, etc). These will ideally be the departments/functions that report to the CEO.
      </p>

      <hr />

      <div className="create-role-header">
        <button
          className={`create-role-toggle-btn ${isCreateFunctionOpen ? 'create-role-toggle-btn--open' : ''}`}
          onClick={() => setIsCreateFunctionOpen(!isCreateFunctionOpen)}
        >
          <span className="create-role-toggle-icon">{isCreateFunctionOpen ? '−' : '+'}</span>
          <span>{isCreateFunctionOpen ? 'Close' : 'Create New Function'}</span>
        </button>
      </div>

      {isCreateFunctionOpen && (
        <div className="create-role-panel">
          <input
            className="input-box"
            type="text"
            placeholder="Function Name"
            value={newDepartment.depart_name}
            onChange={(e) => setNewDepartment({ ...newDepartment, depart_name: e.target.value })}
            onKeyDown={(e) => e.key === 'Enter' && handleAddDepartment(e)}
            autoFocus
          />
          <div className="create-role-actions">
            <button className="add-button" type="button" onClick={handleAddDepartment}>
              Create Function
            </button>
            <button
              type="button"
              className="create-role-cancel-btn"
              onClick={() => {
                setIsCreateFunctionOpen(false);
                setNewDepartment({ depart_name: '' });
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="input-section-title">
        <h2>Organizational Structure</h2>
      </div>

      <table className="objective-table">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th key={header.id}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody>
          {table.getRowModel().rows.length > 0 ? (
            table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={columns.length} className="empty-table-message">
                No functions found.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Edit Modal — portalled to document.body to escape any parent overflow/transform */}
      {isModalOpen && createPortal(
        <div className="modal" style={{position:'fixed',top:0,left:0,width:'100vw',height:'100vh',background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:99999}} onClick={() => { setIsModalOpen(false); setEditDepartment({ department_id: null, depart_name: '' }); }}>
          <div className="modal-content" style={{background:'white',padding:'24px 28px',borderRadius:'10px',width:'400px',maxWidth:'95%',zIndex:100000}} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Edit Function Name</h3>
              <button className="modal-close-btn" type="button" onClick={() => { setIsModalOpen(false); setEditDepartment({ department_id: null, depart_name: '' }); }}>×</button>
            </div>
            <p className="modal-subtitle">Update the name of this function across your organisation.</p>
            <hr className="modal-divider" />
            <label className="modal-label">Function Name</label>
            <input
              type="text"
              value={editDepartment.depart_name}
              onChange={(e) =>
                setEditDepartment({ ...editDepartment, depart_name: e.target.value })
              }
              onKeyDown={(e) => e.key === 'Enter' && handleSaveEdit()}
              autoFocus
            />
            <div className="modal-buttons">
              <button type="button" onClick={handleSaveEdit}>
                Save Changes
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsModalOpen(false);
                  setEditDepartment({ department_id: null, depart_name: '' });
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Confirmation Modal — portalled to document.body to escape any parent overflow/transform */}
      {deleteConfirm.isOpen && createPortal(
        <div className="modal" style={{position:'fixed',top:0,left:0,width:'100vw',height:'100vh',background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:99999}} onClick={() => setDeleteConfirm({ isOpen: false, department: null })}>
          <div className="modal-content modal-delete" style={{background:'white',padding:'24px 28px',borderRadius:'10px',width:'420px',maxWidth:'95%',zIndex:100000}} onClick={(e) => e.stopPropagation()}>
            <div className="modal-delete-header">
              <h3 className="modal-delete-title">Delete Function?</h3>
              <span className="modal-delete-item-name">"{deleteConfirm.department?.depart_name}"</span>
            </div>
            <p className="modal-delete-warning">
              This action <strong>cannot be undone</strong>. Deleting this function will have the following impact:
            </p>
            <ul className="modal-delete-impacts">
              <li>All <strong>roles</strong> linked to this function ({deleteConfirm.department?.role_count || 0} role{deleteConfirm.department?.role_count !== 1 ? 's' : ''}) will be permanently removed.</li>
              <li>Any <strong>department-level objectives</strong> associated with this function will be deleted.</li>
              <li>All <strong>role objectives and competencies</strong> tied to those roles will also be lost.</li>
            </ul>
            <div className="modal-buttons modal-delete-buttons">
              <button
                type="button"
                onClick={() => setDeleteConfirm({ isOpen: false, department: null })}
              >
                Cancel
              </button>
              <button type="button" className="btn-danger" onClick={handleConfirmDelete}>
                Yes, Delete
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default OrganizationObjective;